import { useMemo, type ReactNode } from "react";

import { GroupedUsersGrid } from "./GroupedUsersGrid";
import { useIdentityProviderSources } from "./useIdentityProviderSources";
import { groupingDetailPath, type RetainedSearch } from "./userGrouping";

import type { BucketRow } from "./GroupedUsersGrid";

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/**
 * Lists every SAML provider and cloud org, plus a "Local Users" section for
 * non-SSO users. Expanding one shows the users it provisioned.
 */
export function IdpUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const { samlProviders, cloudOrganizations, loading } =
    useIdentityProviderSources();

  const buckets = useMemo<BucketRow[]>(() => {
    const samlRows: BucketRow[] = samlProviders.map((provider) => ({
      id: provider.id,
      label: provider.displayName || provider.name,
      to: groupingDetailPath({
        kind: "identity-provider",
        providerKind: "saml",
        name: provider.name,
        displayName: provider.displayName || provider.name,
      }),
      memberScope: {
        kind: "identity-provider",
        idpNamespace: provider.idpNamespace,
      },
      detail: "SAML",
    }));

    const cloudRows: BucketRow[] = cloudOrganizations.map((node) => ({
      id: node.id,
      label: node.name,
      to: groupingDetailPath({
        kind: "identity-provider",
        providerKind: "cloud-organization",
        provider: node.provider,
        key: node.key,
        displayName: node.name,
      }),
      memberScope: {
        kind: "identity-provider",
        idpNamespace: node.idpNamespace,
      },
      detail: node.provider,
    }));

    const localRow: BucketRow = {
      id: "__local__",
      label: "Local Users",
      to: null,
      memberScope: { kind: "local" },
      detail: "No identity provider",
    };

    return [...samlRows, ...cloudRows, localRow];
  }, [samlProviders, cloudOrganizations]);

  // Search buckets by the user's `identityLinks`: matched users fall under the
  // provider(s) they're linked to, and unlinked ones under Local Users.
  return (
    <GroupedUsersGrid
      buckets={buckets}
      initialSearchTerm={initialSearchTerm}
      loading={loading}
      onSearchTermChange={onSearchTermChange}
      searchKind="identity-provider"
      tableId="grouped-users-idps"
      toolbarActions={toolbarActions}
      unit="provider"
    />
  );
}
